import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import Spinner from './Spinner';

const BADGE_COLORS = {
    Available: { bg: '#d1e7dd', text: '#0f5132', darkBg: '#1b4332', darkText: '#b7f0c8' },
    LOA: { bg: '#fff3cd', text: '#664d03', darkBg: '#5c4a12', darkText: '#ffe8a3' },
    'Extended LOA': { bg: '#fff3cd', text: '#664d03', darkBg: '#5c4a12', darkText: '#ffe8a3' },
    'Approved leave': { bg: '#cfe2ff', text: '#084298', darkBg: '#1a3358', darkText: '#9ec5fe' },
    Unavailable: { bg: '#f8d7da', text: '#842029', darkBg: '#5c1a1f', darkText: '#f1aeb5' },
    Absent: { bg: '#f8d7da', text: '#842029', darkBg: '#5c1a1f', darkText: '#f1aeb5' },
    'Military leave': { bg: '#e2d9f3', text: '#432874', darkBg: '#3b2a5c', darkText: '#d4c2ff' },
    'Official absence': { bg: '#e2d9f3', text: '#432874', darkBg: '#3b2a5c', darkText: '#d4c2ff' },
    Training: { bg: '#cff4fc', text: '#055160', darkBg: '#164049', darkText: '#9eeaf9' },
    TDY: { bg: '#cff4fc', text: '#055160', darkBg: '#164049', darkText: '#9eeaf9' },
    Unassigned: { bg: '#e2e3e5', text: '#41464b', darkBg: '#3a3a3a', darkText: '#d0d0d0' },
    'Pending Start': { bg: '#cfe2ff', text: '#084298', darkBg: '#1a3358', darkText: '#9ec5fe' },
    Inactive: { bg: '#f8d7da', text: '#842029', darkBg: '#5c1a1f', darkText: '#f1aeb5' },
    Vacancy: { bg: 'transparent', text: '#6c757d', darkBg: 'transparent', darkText: '#9aa6b2' },
};

function getOrgStyles(theme) {
    const isDark = theme === 'dark';
    return {
        isDark,
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginBottom: '16px',
        },
        formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontWeight: 600, fontSize: '13px' },
        input: {
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#e0e0e0' : '#212529',
            fontSize: '14px',
            minWidth: '140px',
        },
        button: {
            padding: '10px 14px',
            backgroundColor: isDark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        secondaryButton: {
            padding: '10px 14px',
            backgroundColor: 'transparent',
            color: isDark ? '#e0e0e0' : '#212529',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        summaryBar: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '10px',
            marginBottom: '18px',
        },
        summaryCard: {
            backgroundColor: isDark ? '#252525' : '#f8f9fa',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
        },
        summaryNumber: { fontSize: '1.4rem', fontWeight: 700 },
        summaryLabel: { fontSize: '0.8rem', color: isDark ? '#9aa6b2' : '#6c757d' },
        board: {
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            paddingBottom: '12px',
            alignItems: 'flex-start',
        },
        column: {
            minWidth: '280px',
            maxWidth: '320px',
            flex: '0 0 280px',
            backgroundColor: isDark ? '#1a1a1a' : '#f1f3f5',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '70vh',
        },
        columnWarn: {
            borderColor: isDark ? '#c9a227' : '#ffc107',
            boxShadow: isDark
                ? '0 0 0 1px rgba(201, 162, 39, 0.35)'
                : '0 0 0 1px rgba(255, 193, 7, 0.45)',
        },
        columnHeader: {
            padding: '14px',
            borderBottom: `1px solid ${isDark ? '#333' : '#dee2e6'}`,
            position: 'sticky',
            top: 0,
            backgroundColor: isDark ? '#222' : '#e9ecef',
            borderRadius: '10px 10px 0 0',
            zIndex: 1,
        },
        columnTitle: { margin: 0, fontSize: '16px', fontWeight: 700 },
        columnMeta: {
            marginTop: '6px',
            fontSize: '12px',
            color: isDark ? '#9aa6b2' : '#6c757d',
            lineHeight: 1.4,
        },
        metrics: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
            marginTop: '8px',
        },
        metricChip: {
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '999px',
            backgroundColor: isDark ? '#333' : '#fff',
            border: `1px solid ${isDark ? '#555' : '#ced4da'}`,
        },
        columnBody: {
            padding: '12px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            flex: 1,
        },
        employeeCard: {
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '12px',
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            color: 'inherit',
            fontFamily: 'inherit',
        },
        vacancyCard: {
            backgroundColor: 'transparent',
            border: `1px dashed ${isDark ? '#666' : '#adb5bd'}`,
            borderRadius: '8px',
            padding: '12px',
            color: isDark ? '#9aa6b2' : '#6c757d',
            fontSize: '13px',
            fontStyle: 'italic',
        },
        nameRow: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '8px',
            alignItems: 'flex-start',
            marginBottom: '6px',
        },
        name: { fontWeight: 700, fontSize: '14px' },
        empNumber: {
            fontSize: '11px',
            color: isDark ? '#9aa6b2' : '#6c757d',
            marginTop: '2px',
        },
        detail: {
            fontSize: '12px',
            color: isDark ? '#c5cdd6' : '#495057',
            lineHeight: 1.45,
        },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
        },
        empty: {
            padding: '24px',
            textAlign: 'center',
            color: isDark ? '#9aa6b2' : '#6c757d',
        },
        hint: {
            color: isDark ? '#9aa6b2' : '#6c757d',
            fontSize: '13px',
            margin: '0 0 14px',
        },
        positionList: {
            marginTop: '8px',
            fontSize: '11px',
            color: isDark ? '#9aa6b2' : '#6c757d',
        },
        warnText: {
            color: isDark ? '#ffc107' : '#996c00',
            fontWeight: 700,
            fontSize: '12px',
            marginTop: '6px',
        },
    };
}

function badgeStyle(badge, isDark) {
    const palette = BADGE_COLORS[badge] || BADGE_COLORS.Unassigned;
    return {
        backgroundColor: isDark ? palette.darkBg : palette.bg,
        color: isDark ? palette.darkText : palette.text,
        border: badge === 'Vacancy' ? `1px dashed ${isDark ? '#666' : '#adb5bd'}` : 'none',
    };
}

function ManningOrgChart({ theme, sites, workCenters, onSelectEmployee }) {
    const styles = useMemo(() => getOrgStyles(theme), [theme]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({
        site: '',
        work_center: '',
        search: '',
        as_of: new Date().toISOString().slice(0, 10),
    });

    const filteredWorkCenters = useMemo(() => {
        if (!filters.site) return workCenters.filter((wc) => wc.is_active);
        return workCenters.filter(
            (wc) => wc.is_active && String(wc.site) === String(filters.site)
        );
    }, [workCenters, filters.site]);

    const loadOrgChart = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (filters.site) params.site = filters.site;
            if (filters.work_center) params.work_center = filters.work_center;
            if (filters.search) params.search = filters.search;
            if (filters.as_of) params.as_of = filters.as_of;
            const res = await api.get('/employees/org-chart/', { params });
            setData(res.data);
        } catch (err) {
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                err?.message ||
                'Failed to load organization chart';
            setError(message);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadOrgChart();
    }, [loadOrgChart]);

    const totals = data?.totals;

    return (
        <div>
            <p style={styles.hint}>
                Flat work-center board as of the selected date. Vacancies use position
                required counts when configured, otherwise work-center minimum staffing.
                Leave and temporary assignments overlay from approved leave chits (Phase 3).
                Remarks stay on the leave record — org chart shows badge and return date only.
            </p>

            <div style={styles.filters}>
                <div style={styles.formGroup}>
                    <label style={styles.label}>As of</label>
                    <input
                        style={styles.input}
                        type="date"
                        value={filters.as_of}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, as_of: e.target.value }))
                        }
                    />
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Site</label>
                    <select
                        style={styles.input}
                        value={filters.site}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                site: e.target.value,
                                work_center: '',
                            }))
                        }
                    >
                        <option value="">All sites</option>
                        {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                                {site.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Work center</label>
                    <select
                        style={styles.input}
                        value={filters.work_center}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                work_center: e.target.value,
                            }))
                        }
                    >
                        <option value="">All work centers</option>
                        {filteredWorkCenters.map((wc) => (
                            <option key={wc.id} value={wc.id}>
                                {wc.code} — {wc.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.formGroup}>
                    <label style={styles.label}>Search</label>
                    <input
                        style={styles.input}
                        placeholder="Name, number, title…"
                        value={filters.search}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, search: e.target.value }))
                        }
                    />
                </div>
                <button type="button" style={styles.button} onClick={loadOrgChart}>
                    Apply
                </button>
                <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() =>
                        setFilters({
                            site: '',
                            work_center: '',
                            search: '',
                            as_of: new Date().toISOString().slice(0, 10),
                        })
                    }
                >
                    Reset
                </button>
            </div>

            {totals && (
                <div style={styles.summaryBar}>
                    {[
                        ['Work centers', totals.work_centers],
                        ['Assigned', totals.assigned],
                        ['Available', totals.available],
                        ['On leave', totals.on_leave],
                        ['Required', totals.required],
                        ['Vacancies', totals.vacancies],
                        ['Below minimum', totals.below_minimum],
                        ['Unassigned', totals.unassigned],
                    ].map(([label, value]) => (
                        <div key={label} style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{value ?? 0}</div>
                            <div style={styles.summaryLabel}>{label}</div>
                        </div>
                    ))}
                </div>
            )}

            {loading && (
                <div style={styles.empty}>
                    <Spinner />
                </div>
            )}

            {!loading && error && <div style={styles.empty}>{error}</div>}

            {!loading && !error && data && (
                <div style={styles.board}>
                    {data.work_centers.map((wc) => (
                        <div
                            key={wc.id}
                            style={{
                                ...styles.column,
                                ...(wc.below_minimum ? styles.columnWarn : {}),
                            }}
                        >
                            <div style={styles.columnHeader}>
                                <h3 style={styles.columnTitle}>
                                    {wc.code} — {wc.name}
                                </h3>
                                <div style={styles.columnMeta}>
                                    {wc.site_name}
                                    {wc.supervisor_name ? ` · Sup: ${wc.supervisor_name}` : ''}
                                </div>
                                <div style={styles.metrics}>
                                    <span style={styles.metricChip}>
                                        {wc.assigned_count}/{wc.required_count || '—'} assigned
                                    </span>
                                    <span style={styles.metricChip}>
                                        {wc.available_count} available
                                    </span>
                                    <span style={styles.metricChip}>
                                        {wc.on_leave_count || 0} on leave
                                    </span>
                                    <span style={styles.metricChip}>
                                        {wc.vacancy_count} vacant
                                    </span>
                                    {wc.fill_rate != null && (
                                        <span style={styles.metricChip}>{wc.fill_rate}% fill</span>
                                    )}
                                </div>
                                {wc.below_minimum && (
                                    <div style={styles.warnText}>
                                        Below minimum staffing ({wc.minimum_staffing})
                                    </div>
                                )}
                                {wc.positions?.length > 0 && (
                                    <div style={styles.positionList}>
                                        {wc.positions.map((pos) => (
                                            <div key={pos.id}>
                                                {pos.title}: {pos.filled_count}/{pos.required_count}
                                                {pos.vacancy_count > 0
                                                    ? ` (${pos.vacancy_count} open)`
                                                    : ''}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div style={styles.columnBody}>
                                {wc.employees.map((emp) => (
                                    <button
                                        key={emp.id}
                                        type="button"
                                        style={styles.employeeCard}
                                        onClick={() => onSelectEmployee?.(emp)}
                                        title="Open employee record"
                                    >
                                        <div style={styles.nameRow}>
                                            <div>
                                                <div style={styles.name}>{emp.display_name}</div>
                                                <div style={styles.empNumber}>
                                                    #{emp.employee_number}
                                                </div>
                                            </div>
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    ...badgeStyle(
                                                        emp.operational_badge,
                                                        styles.isDark
                                                    ),
                                                }}
                                            >
                                                {emp.operational_badge}
                                            </span>
                                        </div>
                                        <div style={styles.detail}>
                                            {(emp.position_title || emp.job_title || 'No title') +
                                                (emp.shift_name ? ` · ${emp.shift_name}` : '')}
                                        </div>
                                        {emp.supervisor_name && (
                                            <div style={styles.detail}>
                                                Supervisor: {emp.supervisor_name}
                                            </div>
                                        )}
                                        {emp.return_date && (
                                            <div style={styles.detail}>
                                                Return: {emp.return_date}
                                            </div>
                                        )}
                                        {emp.telephone && (
                                            <div style={styles.detail}>{emp.telephone}</div>
                                        )}
                                    </button>
                                ))}

                                {(() => {
                                    const maxVacancyCards = 8;
                                    const shown = Math.min(wc.vacancy_count, maxVacancyCards);
                                    const remaining = wc.vacancy_count - shown;
                                    return (
                                        <>
                                            {Array.from({ length: shown }).map((_, index) => (
                                                <div
                                                    key={`vacancy-${wc.id}-${index}`}
                                                    style={styles.vacancyCard}
                                                >
                                                    <div style={styles.nameRow}>
                                                        <div style={styles.name}>Open billet</div>
                                                        <span
                                                            style={{
                                                                ...styles.badge,
                                                                ...badgeStyle('Vacancy', styles.isDark),
                                                            }}
                                                        >
                                                            Vacancy
                                                        </span>
                                                    </div>
                                                    <div>Unfilled authorized position</div>
                                                </div>
                                            ))}
                                            {remaining > 0 && (
                                                <div style={styles.vacancyCard}>
                                                    +{remaining} more open billets
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {wc.employees.length === 0 && wc.vacancy_count === 0 && (
                                    <div style={styles.empty}>No assigned employees</div>
                                )}
                            </div>
                        </div>
                    ))}

                    {(data.unassigned?.length > 0) && (
                        <div style={styles.column}>
                            <div style={styles.columnHeader}>
                                <h3 style={styles.columnTitle}>Unassigned</h3>
                                <div style={styles.columnMeta}>
                                    Active or pending employees without a current primary assignment
                                </div>
                                <div style={styles.metrics}>
                                    <span style={styles.metricChip}>
                                        {data.unassigned.length} people
                                    </span>
                                </div>
                            </div>
                            <div style={styles.columnBody}>
                                {data.unassigned.map((emp) => (
                                    <button
                                        key={emp.id}
                                        type="button"
                                        style={styles.employeeCard}
                                        onClick={() => onSelectEmployee?.(emp)}
                                    >
                                        <div style={styles.nameRow}>
                                            <div>
                                                <div style={styles.name}>{emp.display_name}</div>
                                                <div style={styles.empNumber}>
                                                    #{emp.employee_number}
                                                </div>
                                            </div>
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    ...badgeStyle(
                                                        emp.operational_badge,
                                                        styles.isDark
                                                    ),
                                                }}
                                            >
                                                {emp.operational_badge}
                                            </span>
                                        </div>
                                        <div style={styles.detail}>
                                            {emp.job_title || 'No title'}
                                        </div>
                                        {emp.telephone && (
                                            <div style={styles.detail}>{emp.telephone}</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.work_centers.length === 0 && data.unassigned?.length === 0 && (
                        <div style={styles.empty}>
                            No work centers or employees match the current filters.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ManningOrgChart;
